import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BYOK_PROVIDERS, byokProviderMeta, type ByokProviderId, type ByokProviderRecord, type ByokSettings } from "@currencycore/shared";
import { trackByokSave, trackByokToggle, trackByokDelete } from "../lib/analytics.ts";
import {
  addByokProvider,
  deleteByokProvider,
  getByokSettings,
  reorderByokProviders,
  setByokSettings,
  testByokConfig,
  testByokProvider,
  updateByokProvider,
} from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";
import { Select } from "../components/Select.tsx";

/**
 * BYOK — Bring Your Own (AI provider) Keys, for Growth+ orgs. Lets an org run the
 * /ai endpoint on its OWN provider/model. When BYOK is active and an external
 * provider answers, the call counts as a normal API request; only our-provider
 * calls (BYOK off, or a fallback) consume an AI credit. Provider keys are
 * encrypted at rest and never returned to the browser (see /orgs/:id/byok).
 */

const DOCS_URL = "https://currency-core.com/docs/byok";

type TestState = { ok: boolean; error?: string } | "pending" | null;

const EMPTY_FORM = {
  provider: "openai" as ByokProviderId,
  label: "",
  apiKey: "",
  model: "",
  baseUrl: "",
  apiVersion: "",
};

export function AiProvidersPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [settings, setSettings] = useState<ByokSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formTest, setFormTest] = useState<TestState>(null);
  const [rowTest, setRowTest] = useState<Record<string, TestState>>({});
  const formRef = useRef<HTMLDivElement>(null);
  // Tool-call cap input (string so empty = unlimited); synced from settings.
  const [toolCapInput, setToolCapInput] = useState("");

  const meta = useMemo(() => byokProviderMeta(form.provider)!, [form.provider]);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    try {
      setSettings(await getByokSettings(orgId));
    } catch {
      /* best-effort */
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Mirror the saved cap into the input (blank = unlimited).
  useEffect(() => {
    if (settings) setToolCapInput(settings.maxToolCalls == null ? "" : String(settings.maxToolCalls));
  }, [settings?.maxToolCalls]);

  /** Persist the tool-call cap on blur. Blank = unlimited. */
  async function saveToolCap() {
    if (!orgId || !settings) return;
    const trimmed = toolCapInput.trim();
    let mtc: number | null;
    if (trimmed === "") {
      mtc = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isInteger(n) || n < 1 || n > 1000) {
        toast("Enter a whole number from 1 to 1000, or leave blank for unlimited.");
        setToolCapInput(settings.maxToolCalls == null ? "" : String(settings.maxToolCalls));
        return;
      }
      mtc = n;
    }
    if (mtc === (settings.maxToolCalls ?? null)) return; // unchanged
    setSettings({ ...settings, maxToolCalls: mtc });
    try {
      await setByokSettings(orgId, { enabled: settings.enabled, fallback: settings.fallback, maxToolCalls: mtc });
      toast("Tool-call limit updated.", true);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't update the limit.");
      await refresh();
    }
  }

  function patchForm(next: Partial<typeof EMPTY_FORM>) {
    setFormTest(null);
    setForm((f) => ({ ...f, ...next }));
  }

  function onProviderChange(id: ByokProviderId) {
    // Reset the provider-specific fields so a stale base URL/version can't leak across.
    patchForm({ provider: id, baseUrl: "", apiVersion: "" });
  }

  /** Assemble the write payload from the form (server validates again). */
  function formPayload() {
    return {
      provider: form.provider,
      label: form.label.trim() || undefined,
      apiKey: form.apiKey.trim(),
      model: form.model.trim(),
      baseUrl: form.baseUrl.trim() || undefined,
      apiVersion: form.apiVersion.trim() || undefined,
    };
  }

  async function toggleSetting(next: { enabled?: boolean; fallback?: boolean }) {
    if (!orgId || !settings) return;
    const body = { enabled: settings.enabled, fallback: settings.fallback, ...next };
    setSettings({ ...settings, ...body });
    try {
      await setByokSettings(orgId, body);
      toast("BYOK settings updated.", true);
      if ("enabled" in next) trackByokToggle(next.enabled!);
    } catch (err) {
      setSettings(settings); // revert
      toast(err instanceof Error ? err.message : "Couldn't update settings.");
    }
  }

  async function runFormTest() {
    if (!orgId) return;
    // Inline test needs the actual key in hand. When editing without entering a
    // new key, there's nothing to send — save first, then use Test on the row.
    if (!form.apiKey.trim() || !form.model.trim()) {
      toast(editingId ? "Enter a model and a new key to test, or save and use Test on the row." : "Enter an API key and a model first.");
      return;
    }
    setFormTest("pending");
    try {
      setFormTest(await testByokConfig(orgId, formPayload()));
    } catch (err) {
      setFormTest({ ok: false, error: err instanceof Error ? err.message : "Test failed." });
    }
  }

  /** Load a provider's values into the form for editing (key stays blank = keep). */
  function startEdit(p: ByokProviderRecord) {
    setEditingId(p.id);
    setFormTest(null);
    setForm({
      provider: p.provider,
      label: p.label ?? "",
      apiKey: "",
      model: p.model,
      baseUrl: p.baseUrl ?? "",
      apiVersion: p.apiVersion ?? "",
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function resetForm() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormTest(null);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setSaving(true);
    try {
      if (editingId) {
        // Every field is editable; the key is replace-only — send it only when a
        // new one was entered, otherwise the server keeps the stored key.
        const body = {
          provider: form.provider,
          label: form.label.trim(),
          model: form.model.trim(),
          baseUrl: form.baseUrl.trim(),
          apiVersion: form.apiVersion.trim(),
          ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
        };
        await updateByokProvider(orgId, editingId, body);
        toast("Provider updated.", true);
      } else {
        await addByokProvider(orgId, formPayload());
        toast("Provider added.", true);
      }
      resetForm();
      await refresh();
      trackByokSave(settings?.providers?.length ?? 1);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't save the provider.");
    } finally {
      setSaving(false);
    }
  }

  async function testRow(p: ByokProviderRecord) {
    if (!orgId) return;
    setRowTest((s) => ({ ...s, [p.id]: "pending" }));
    try {
      const res = await testByokProvider(orgId, p.id);
      setRowTest((s) => ({ ...s, [p.id]: res }));
      toast(res.ok ? "Connection OK." : res.error || "Connection failed.", res.ok);
      await refresh();
    } catch (err) {
      setRowTest((s) => ({ ...s, [p.id]: { ok: false, error: "Test failed." } }));
      toast(err instanceof Error ? err.message : "Test failed.");
    }
  }

  async function toggleActive(p: ByokProviderRecord) {
    if (!orgId) return;
    try {
      await updateByokProvider(orgId, p.id, { active: !p.active });
      await refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't update the provider.");
    }
  }

  async function remove(p: ByokProviderRecord) {
    if (!orgId) return;
    try {
      await deleteByokProvider(orgId, p.id);
      toast("Provider removed.", true);
      trackByokDelete();
      await refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't remove the provider.");
    }
  }

  /** Move a provider up/down to set its try priority (index 0 = primary). */
  async function move(index: number, dir: -1 | 1) {
    if (!orgId || !settings) return;
    const list = settings.providers;
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((x) => x.id);
    const tmp = ids[index]!;
    ids[index] = ids[j]!;
    ids[j] = tmp;
    try {
      const updated = await reorderByokProviders(orgId, ids);
      setSettings((s) => (s ? { ...s, providers: updated } : s));
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't reorder providers.");
    }
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton-page">
          {[0, 1].map((i) => <div key={i} className="skeleton" style={{ height: "5rem" }} />)}
        </div>
      </div>
    );
  }

  const available = settings?.available ?? false;
  const configured = settings?.configured ?? false;
  const providers = settings?.providers ?? [];
  // What /ai will actually route to: an active provider whose stored key still
  // decrypts (keyOk !== false). If BYOK is on but this is empty, /ai silently
  // uses CurrencyCore's models — surface that loudly.
  const usableProviders = providers.filter((p) => p.active && p.keyOk !== false);
  const hasUnreadableKey = providers.some((p) => p.keyOk === false);
  const enabledButNotRouting = !!settings?.enabled && configured && usableProviders.length === 0;

  return (
    <div className="animate-fade-in">
      <div className="page-head rise">
        <div>
          <h1>AI Providers (BYOK)</h1>
          <p className="sub">
            Run the <code>/ai</code> endpoint on your own AI provider and model. Your keys, your bill:{" "}
            <a href={DOCS_URL} target="_blank" rel="noreferrer">how to connect a provider</a>.
          </p>
        </div>
      </div>

      {!available ? (
        <div className="notice accent rise">
          <span className="ic">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
            </svg>
          </span>
          <span>
            Bring your own keys is a <strong style={{ color: "var(--text)" }}>Growth</strong> feature.
            Upgrade this organization to connect OpenAI, Anthropic, Google Gemini, Azure, OpenRouter, and more.
          </span>
        </div>
      ) : (
        <>
          {!configured && (
            <div className="notice rise">
              <span>BYOK isn't fully provisioned on this deployment yet. You can configure providers, but routing stays off until support enables it.</span>
            </div>
          )}

          {/* Routing status — why /ai might still be using our models */}
          {enabledButNotRouting && (
            <div className="notice rise" style={{ borderColor: "var(--danger)", color: "var(--text)" }}>
              <span>
                <strong>BYOK is on, but no provider is usable</strong> right now, so <code>/ai</code> is answering with
                CurrencyCore's models.{" "}
                {hasUnreadableKey
                  ? "A stored key can't be read (the encryption key was likely rotated after it was saved). Open Edit on the flagged provider and re-enter the key."
                  : providers.length === 0
                    ? "Add a provider below."
                    : "Enable a provider below."}
              </span>
            </div>
          )}

          {/* Metering explainer */}
          <div className="notice rise">
            <span>
              When BYOK is on and one of your providers answers, the call counts as a normal{" "}
              <strong>API request</strong> (your monthly quota); <strong>no AI credit</strong> is used.
              AI credits are consumed only when CurrencyCore's own models answer (BYOK off, or a fallback).
            </span>
          </div>

          {/* Master toggles */}
          <div className="card rise">
            <div className="card-head"><h2>BYOK routing</h2></div>
            <div className="card-pad" style={{ display: "grid", gap: 18 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <p className="strong" style={{ margin: 0 }}>Use my providers for /ai</p>
                  <p className="muted" style={{ margin: "2px 0 0" }}>
                    Route /ai requests to your active providers below, top to bottom. The first one
                    that answers is used, and whatever it returns is the result.
                  </p>
                </div>
                <label className="switch" aria-label="Use my providers">
                  <input type="checkbox" checked={settings!.enabled} onChange={(e) => void toggleSetting({ enabled: e.target.checked })} />
                  <span className="track" /><span className="thumb" />
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, opacity: settings!.enabled ? 1 : 0.5 }}>
                <div>
                  <p className="strong" style={{ margin: 0 }}>Fall back to CurrencyCore</p>
                  <p className="muted" style={{ margin: "2px 0 0" }}>
                    If all your providers fail (unreachable, timeout, or an error response), answer
                    with our models instead (consumes 1 AI credit). When off, the request returns an error.
                  </p>
                </div>
                <label className="switch" aria-label="Fall back to CurrencyCore">
                  <input type="checkbox" checked={settings!.fallback} disabled={!settings!.enabled} onChange={(e) => void toggleSetting({ fallback: e.target.checked })} />
                  <span className="track" /><span className="thumb" />
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <p className="strong" style={{ margin: 0 }}>Max tool calls per request</p>
                  <p className="muted" style={{ margin: "2px 0 0" }}>
                    On your own provider, each /ai request bills <strong>one API request per tool call</strong>.
                    Cap how many a single request may run. Leave blank for unlimited (still bounded by the timeout).
                  </p>
                </div>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  className="form-input mono"
                  style={{ width: 96, textAlign: "right" }}
                  placeholder="∞"
                  aria-label="Max tool calls per request"
                  value={toolCapInput}
                  onChange={(e) => setToolCapInput(e.target.value)}
                  onBlur={() => void saveToolCap()}
                />
              </div>
              {settings!.enabled && providers.filter((p) => p.active).length === 0 && (
                <p className="hint" style={{ margin: 0 }}>
                  BYOK is on but you have no active providers. /ai will use {settings!.fallback ? "CurrencyCore's models (1 AI credit each)" : "nothing and return an error"} until you add one.
                </p>
              )}
            </div>
          </div>

          {/* Providers list */}
          <div className="card mt rise">
            <div className="card-head">
              <h2>Your providers</h2>
              <span className="hint mono">{providers.length} configured</span>
            </div>
            {providers.length === 0 ? (
              <div className="card-pad" style={{ textAlign: "center", padding: "32px 20px" }}>
                <p className="muted">No providers yet. Add one below.</p>
              </div>
            ) : (
              <>
                {providers.length > 1 && (
                  <div className="card-pad" style={{ paddingBottom: 8 }}>
                    <p className="hint" style={{ margin: 0 }}>
                      Tried top to bottom: <code>/ai</code> uses the first provider that answers, whatever it returns. The
                      next is tried only when one <strong>fails</strong> (unreachable, timeout, or an error), never because of
                      the content of a successful answer. Use ▲▼ to set the order.
                    </p>
                  </div>
                )}
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr><th style={{ width: 44 }} aria-label="Order" /><th>Provider</th><th>Model</th><th>Status</th><th /></tr>
                    </thead>
                    <tbody>
                    {providers.map((p, i) => {
                      const m = byokProviderMeta(p.provider);
                      const rt = rowTest[p.id];
                      return (
                        <tr key={p.id}>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <button className="btn btn-sm" type="button" style={{ padding: "0 7px", lineHeight: 1.5 }} title="Move up (higher priority)" aria-label="Move up" disabled={i === 0} onClick={() => void move(i, -1)}>▲</button>
                              <button className="btn btn-sm" type="button" style={{ padding: "0 7px", lineHeight: 1.5 }} title="Move down (lower priority)" aria-label="Move down" disabled={i === providers.length - 1} onClick={() => void move(i, 1)}>▼</button>
                            </div>
                          </td>
                          <td>
                            <span className="strong">{m?.label ?? p.provider}</span>
                            {p.label && <span className="muted" style={{ display: "block", fontSize: 12 }}>{p.label}</span>}
                          </td>
                          <td><span className="muted mono" style={{ fontSize: 12 }}>{p.model}</span></td>
                          <td>
                            {p.keyOk === false ? (
                              <span className="badge badge-danger" style={{ fontSize: 10 }} title="The stored key can't be decrypted. Edit this provider and re-enter the key.">Key unreadable</span>
                            ) : !p.active ? (
                              <span className="badge badge-neutral" style={{ fontSize: 10 }}>Disabled</span>
                            ) : rt === "pending" ? (
                              <span className="badge badge-neutral" style={{ fontSize: 10 }}>Testing…</span>
                            ) : p.lastTestOk === true ? (
                              <span className="badge badge-accent" style={{ fontSize: 10 }}>Tested OK</span>
                            ) : p.lastTestOk === false ? (
                              <span className="badge badge-danger" style={{ fontSize: 10 }}>Test failed</span>
                            ) : (
                              <span className="badge badge-neutral" style={{ fontSize: 10 }}>Untested</span>
                            )}
                          </td>
                          <td className="col-right" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            <button className="btn btn-sm" type="button" onClick={() => startEdit(p)}>Edit</button>
                            <button className="btn btn-sm" type="button" onClick={() => void testRow(p)} disabled={rt === "pending"}>Test</button>
                            <button className="btn btn-sm" type="button" onClick={() => void toggleActive(p)}>{p.active ? "Disable" : "Enable"}</button>
                            <button className="btn btn-sm btn-danger" type="button" onClick={() => void remove(p)}>Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Add / edit provider */}
          <div className="card mt rise" ref={formRef}>
            <div className="card-head">
              <h2>{editingId ? "Edit provider" : "Add a provider"}</h2>
              {editingId && <span className="hint mono">editing</span>}
            </div>
            <form className="card-pad" onSubmit={(e) => void save(e)}>
              <div className="form-row">
                <span className="form-label">Provider</span>
                <Select
                  ariaLabel="Provider"
                  value={form.provider}
                  options={BYOK_PROVIDERS.map((p) => ({ value: p.id, label: p.label }))}
                  onChange={(v) => onProviderChange(v as ByokProviderId)}
                />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="byok-label">Label <span className="muted">(optional)</span></label>
                <input id="byok-label" className="form-input" placeholder="e.g. Production key" value={form.label} onChange={(e) => patchForm({ label: e.target.value })} />
              </div>
              {meta.needsBaseUrl && (
                <div className="form-row">
                  <label className="form-label" htmlFor="byok-base">Base URL</label>
                  <input id="byok-base" className="form-input" placeholder={form.provider === "azure" ? "https://<resource>.openai.azure.com" : "https://your-endpoint/v1"} value={form.baseUrl} onChange={(e) => patchForm({ baseUrl: e.target.value })} required />
                </div>
              )}
              {meta.needsApiVersion && (
                <div className="form-row">
                  <label className="form-label" htmlFor="byok-version">API version</label>
                  <input id="byok-version" className="form-input" placeholder="2024-06-01" value={form.apiVersion} onChange={(e) => patchForm({ apiVersion: e.target.value })} required />
                </div>
              )}
              <div className="form-row">
                <label className="form-label" htmlFor="byok-model">Model</label>
                <input id="byok-model" className="form-input mono" placeholder={meta.modelExample} value={form.model} onChange={(e) => patchForm({ model: e.target.value })} required />
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="byok-key">
                  API key {editingId && <span className="muted">(leave blank to keep the saved key)</span>}
                </label>
                <input
                  id="byok-key"
                  className="form-input mono"
                  type="password"
                  autoComplete="off"
                  placeholder={editingId ? "Enter a new key to replace it, or leave blank" : "Stored encrypted, never shown again"}
                  value={form.apiKey}
                  onChange={(e) => patchForm({ apiKey: e.target.value })}
                  required={!editingId}
                />
              </div>

              {formTest && formTest !== "pending" && (
                <div className={`notice ${formTest.ok ? "accent" : ""}`} style={{ marginTop: 4 }}>
                  <span>{formTest.ok ? "Connection succeeded. This provider is reachable." : `Connection failed: ${formTest.error || "unknown error"}`}</span>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button className="btn" type="button" onClick={() => void runFormTest()} disabled={formTest === "pending"}>
                  {formTest === "pending" ? "Testing…" : "Test connection"}
                </button>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Save changes" : "Add provider"}
                </button>
                {editingId && (
                  <button className="btn" type="button" onClick={resetForm} disabled={saving}>Cancel</button>
                )}
              </div>
              <p className="hint" style={{ marginTop: 12 }}>
                You must specify the exact model id. CurrencyCore never picks a default. See{" "}
                <a href={DOCS_URL} target="_blank" rel="noreferrer">the BYOK guide</a> for each provider's setup, supported models, and metering rules.
              </p>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
